// src/pages/Home.js
import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import "./Home.css";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Home() {
  const [hero, setHero] = useState(null);
  const [aboutUsImages, setAboutUsImages] = useState([]);
  const [popupImage, setPopupImage] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Query parameters
  const isHowItWorks = new URLSearchParams(location.search).get("how") === "1";
  const isAboutUs = new URLSearchParams(location.search).get("about") === "1";

  useEffect(() => {
    async function fetchHero() {
      const ref = doc(db, "siteSettings", "global");
      const snap = await getDoc(ref);

      if (snap.exists()) {
        setHero(snap.data());
      } else {
        console.warn("No global site settings document found");

        setHero({
          heroTitle:
            "Interactive mystery trails across Ireland — explore, solve, and enjoy the craic!",
          heroSubtitle:
            "Uncover puzzles, follow clues, and discover hidden history.",
          heroButtonText: "Browse Trails",
          howitworks:
            "This is where the 'How It Works' text will appear from Firestore.",
          aboutus:
            "This is placeholder About Us text. Add 'aboutus' to Firestore.",
        });
      }
    }

    fetchHero();
  }, []);

  // Fetch About Us images from Firebase Storage
  useEffect(() => {
    async function fetchAboutUsImages() {
      try {
        const imagesRef = ref(storage, "aboutus-images");
        const result = await listAll(imagesRef);
        
        const urls = await Promise.all(
          result.items.map(async (itemRef) => {
            const url = await getDownloadURL(itemRef);
            return url;
          })
        );
        
        setAboutUsImages(urls);
      } catch (error) {
        console.error("Error fetching about us images:", error);
      }
    }

    fetchAboutUsImages();
  }, []);

  if (!hero) return null;

  return (
    <div className="home-hero">
      <div className="hero-overlay"></div>

      <div className="hero-content">
        {/* Title always shown */}
        <h1 className="hero-title">{hero.heroTitle}</h1>

       {/* Default subtitle */}
{!isHowItWorks && !isAboutUs && (
  <div
    className="hero-subtitle"
    style={{ whiteSpace: "pre-line" }}
    dangerouslySetInnerHTML={{ __html: hero.heroSubtitle }}
  />
)}

        {/* How It Works HTML */}
        {isHowItWorks && (
          <div
            className="hero-about-content"
            style={{ 
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxWidth: "800px",
              margin: "0 auto"
            }}
            dangerouslySetInnerHTML={{ __html: hero.howitworks }}
          />
        )}

        {/* About Us HTML */}
        {isAboutUs && (
          <>
            <div
              className="hero-about-content"
              style={{ 
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: "800px",
                margin: "0 auto"
              }}
              dangerouslySetInnerHTML={{ __html: hero.aboutus }}
            />
            
            {/* About Us Images */}
            {aboutUsImages.length > 0 && (
              <div 
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "20px",
                  maxWidth: "1200px",
                  margin: "30px auto 0",
                  padding: "0 20px",
                  justifyContent: "center"
                }}
              >
                {aboutUsImages.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`About us ${index + 1}`}
                    style={{
                      width: "200px",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s"
                    }}
                    onClick={() => setPopupImage(url)}
                    onMouseOver={(e) => {
                      e.target.style.transform = "scale(1.05)";
                      e.target.style.boxShadow = "0 6px 12px rgba(0,0,0,0.5)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "0 4px 6px rgba(0,0,0,0.3)";
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Image Popup Modal */}
        {popupImage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              cursor: "pointer",
              padding: "20px"
            }}
            onClick={() => setPopupImage(null)}
          >
            <img
              src={popupImage}
              alt="Enlarged view"
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.8)"
              }}
            />
          </div>
        )}

        {/* Browse Trails ALWAYS visible */}
        <Link to="/trails" className="hero-button">
          {hero.heroButtonText}
        </Link>

        {/* Toggle buttons */}
        <div className="toggle-buttons">

          {/* Default screen */}
          {!isHowItWorks && !isAboutUs && (
            <>
              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/?how=1")}
              >
                How It Works
              </button>

              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/?about=1")}
              >
                About Us
              </button>
            </>
          )}

          {/* How It Works view */}
          {isHowItWorks && (
            <>
              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/")}
              >
                Home
              </button>

              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/?about=1")}
              >
                About Us
              </button>
            </>
          )}

          {/* About Us view */}
          {isAboutUs && (
            <>
              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/")}
              >
                Home
              </button>

              <button
                className="hero-button transparent-button"
                onClick={() => navigate("/?how=1")}
              >
                How It Works
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}